import React, { useEffect, useState } from 'react';
import { aiApi } from '@/utils/api';
import { Sparkles, RefreshCw, Play, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

export default function AIBriefTab({ project, clientId }) {
  const [brief, setBrief] = useState('');
  const [briefUpdated, setBriefUpdated] = useState('');
  const [generating, setGenerating] = useState(false);
  const [skills, setSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [runOutput, setRunOutput] = useState('');
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);

  useEffect(() => {
    if (clientId) {
      aiApi.latestBrief(clientId).then(r => {
        setBrief(r.data.brief || '');
        setBriefUpdated(r.data.updated_at || '');
      }).catch(() => {});
    }
    aiApi.skills().then(r => setSkills(r.data)).catch(() => {});
    aiApi.runs({ project_id: project?.id }).then(r => setRuns(r.data)).catch(() => {});
  }, [clientId, project?.id]);

  const generateBrief = async () => {
    if (!clientId) { toast.error('No client ID available'); return; }
    setGenerating(true);
    setBrief('');
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL + '/api';
      const response = await fetch(`${apiBase}/ai/brief/${clientId}`, {
        method: 'POST', credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split('\n')) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) { accumulated += data.text; setBrief(accumulated); }
              if (data.error) throw new Error(data.error);
            } catch {}
          }
        }
      }
      toast.success('Brief generated');
    } catch (err) {
      toast.error(err.message || 'Failed to generate brief. Configure Claude API key in Settings.');
    } finally { setGenerating(false); }
  };

  const runSkill = async () => {
    if (!selectedSkill) { toast.error('Select a skill'); return; }
    setRunning(true);
    setRunOutput('');
    try {
      const r = await aiApi.runSkill({
        skill_id: selectedSkill,
        project_id: project?.id,
        client_id: clientId,
      });
      setRunOutput(r.data.output || '');
      setRuns(prev => [r.data, ...prev]);
      toast.success('Skill run complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Skill run failed. Configure Claude API key in Settings.');
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Client Brief */}
      <div className="card-base p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={15} style={{ color: '#006080' }} />
            <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>AI Client Brief</h3>
            {briefUpdated && (
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                Updated {new Date(briefUpdated).toLocaleDateString()}
              </span>
            )}
          </div>
          <button
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-btn transition-colors"
            style={{ background: '#E6F1F4', color: '#006080' }}
            onClick={generateBrief}
            disabled={generating}
            data-testid="generate-brief-btn"
          >
            <RefreshCw size={12} className={generating ? 'animate-spin' : ''} />
            {generating ? 'Generating...' : 'Regenerate'}
          </button>
        </div>

        {brief ? (
          <div className="text-sm leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto" style={{ color: 'var(--ink)' }} data-testid="ai-brief-output">
            {brief}
          </div>
        ) : (
          <div className="py-6 text-center">
            <Sparkles size={24} style={{ color: 'var(--border)', margin: '0 auto 8px' }} />
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {generating ? 'Generating brief with Claude AI...' : 'Click Regenerate to generate an AI-powered client brief'}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Requires Claude API key in Settings</p>
          </div>
        )}
      </div>

      {/* Run AI Skill */}
      {skills.length > 0 && (
        <div className="card-base p-4">
          <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--ink)' }}>Run AI Skill</h3>
          <div className="flex gap-2">
            <select
              className="input-agency text-sm flex-1"
              value={selectedSkill}
              onChange={e => setSelectedSkill(e.target.value)}
              data-testid="skill-select"
            >
              <option value="">Select skill...</option>
              {skills.map(s => <option key={s.id} value={s.id}>{s.name} ({s.skill_type})</option>)}
            </select>
            <button
              className="btn-primary text-xs flex items-center gap-1.5 px-4"
              onClick={runSkill}
              disabled={running || !selectedSkill}
              data-testid="run-skill-button"
            >
              <Play size={12} />
              {running ? 'Running...' : 'Run'}
            </button>
          </div>

          {runOutput && (
            <div className="mt-3 p-3 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Output</p>
              <div className="text-sm whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto" style={{ color: 'var(--ink)' }}>
                {runOutput}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Previous runs */}
      {runs.length > 0 && (
        <div className="card-base overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3"
            onClick={() => setShowRuns(!showRuns)}
            data-testid="toggle-skill-runs"
          >
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Previous Runs ({runs.length})</span>
            {showRuns ? <ChevronUp size={14} style={{ color: 'var(--muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--muted)' }} />}
          </button>
          {showRuns && (
            <div className="border-t divide-y" style={{ borderColor: 'var(--border)' }}>
              {runs.slice(0, 5).map(r => (
                <div key={r.id} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{r.skill_name || 'Brief'}</span>
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs line-clamp-2" style={{ color: 'var(--muted)' }}>{r.output?.slice(0, 150)}...</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
